import React, { useState, useEffect, useMemo } from 'react';
import './WordReveal.css';

/**
 * WordReveal - Word-by-word animation that preserves formatting.
 *
 * Usage:
 *   <WordReveal animate={true}>{formattedContent}</WordReveal>
 *   <WordReveal animate={isNew} speed={60}>{renderMarkdown(text)}</WordReveal>
 */
function WordReveal({
  children,
  text, // Legacy support - plain text
  animate = true,
  speed = 50,  // ms per word
  onComplete,
  className = ''
}) {
  const [visibleWords, setVisibleWords] = useState(animate ? 0 : Infinity);

  // Count total words in the content
  const totalWords = useMemo(() => {
    if (text) {
      return text.split(/\s+/).filter(w => w).length;
    }

    let count = 0;
    const countWords = (node) => {
      if (typeof node === 'string') {
        count += node.split(/\s+/).filter(w => w).length;
      } else if (Array.isArray(node)) {
        node.forEach(countWords);
      } else if (React.isValidElement(node) && node.props.children) {
        countWords(node.props.children);
      }
    };
    countWords(children);
    return count;
  }, [children, text]);

  useEffect(() => {
    if (!animate) {
      setVisibleWords(Infinity);
      return;
    }

    // Reset when content changes
    setVisibleWords(0);

    const interval = setInterval(() => {
      setVisibleWords(prev => {
        if (prev >= totalWords) {
          clearInterval(interval);
          onComplete?.();
          return prev;
        }
        return prev + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [children, text, animate, totalWords, speed, onComplete]);

  // For legacy plain text usage
  if (text && !children) {
    const words = text.split(/(\s+)/);
    let wordIndex = 0;

    return (
      <span className={`word-reveal-container ${className}`}>
        {words.map((word, i) => {
          if (!word.trim()) {
            return <span key={i}>{word}</span>;
          }
          const isVisible = wordIndex < visibleWords;
          wordIndex++;
          return (
            <span key={i} className={`word-reveal-word ${isVisible ? 'visible' : ''}`}>
              {word}
            </span>
          );
        })}
      </span>
    );
  }

  // For formatted React content - wrap each word in visibility spans
  let wordIndex = 0;

  const wrapWords = (node, key = 0) => {
    // Handle strings - wrap each word
    if (typeof node === 'string') {
      const parts = node.split(/(\s+)/);
      return parts.map((part, i) => {
        if (!part.trim()) {
          return <span key={`${key}-${i}`}>{part}</span>;
        }
        const isVisible = wordIndex < visibleWords;
        wordIndex++;
        return (
          <span key={`${key}-${i}`} className={`word-reveal-word ${isVisible ? 'visible' : ''}`}>
            {part}
          </span>
        );
      });
    }

    // Handle arrays
    if (Array.isArray(node)) {
      return node.map((child, i) => wrapWords(child, `${key}-${i}`));
    }

    // Handle React elements - clone with wrapped children
    if (React.isValidElement(node)) {
      const wrappedChildren = node.props.children ? wrapWords(node.props.children, `${key}-c`) : null;
      return React.cloneElement(node, { key: `${key}-el`, children: wrappedChildren });
    }

    // Handle null/undefined
    if (node == null) {
      return null;
    }

    // Handle numbers, etc
    return node;
  };

  return (
    <div className={`word-reveal-container ${className}`}>
      {wrapWords(children)}
    </div>
  );
}

export default WordReveal;
